import { Core } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Flex, IFlexProps } from "@faire/web/slate/Layout";
import { getSpacing, SpacerMultipliersKey } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import {
  isResponsiveProp,
  ResponsiveProp,
} from "@faire/web/slate/Theme/ResponsiveProp";
import { ResponsiveValues } from "@faire/web/slate/Theme/ResponsiveValues";
import { TypographyVariants } from "@faire/web/slate/Typography/Types";
import React from "react";
import styled, { css } from "styled-components";

import {
  showroomVettingCriteriaInfo,
  VETTING_CRITERIA,
} from "@faire/retailer/features/Showroom/ShowroomVettingCriteria/ShowroomVettingCriteriaInfo";
import { SingleShowroomVettingCriteria } from "@faire/retailer/features/Showroom/ShowroomVettingCriteria/SingleShowroomVettingCriteria";

interface IProps {
  className?: string;
  /** Whether icon and text will display in column. */
  column?: boolean;
  /** Whether a fog border will display between each vetting criteria. */
  border?: boolean;
  /** Whether to display detailed description text for the criteria. */
  showDescriptionText?: boolean;
  /** Gap between each vetting criteria. */
  gap?: ResponsiveProp<number | SpacerMultipliersKey>;
  /** Padding above and below criteria */
  verticalPadding?: ResponsiveProp<number | SpacerMultipliersKey>;
  /** Color of icon and text. */
  color?: string;
  /** Color of border between criteria. */
  borderColor?: string;
  /** Vetting Value name typography variants. */
  titleTypographyVariants?: TypographyVariants;
  /**
   * Vetting criteria we want to display.
   * The order of the list will be the display order.
   */
  vettingCriteriaList?: VETTING_CRITERIA[];
  /** Where we want to align each icon to text. Align items to center by default. */
  align?: string;
  /** Where we want to align text. Text is aligned to left by default. */
  alignText?: string;
}

export const ShowroomVettingCriteria: React.FC<IProps> = ({
  className,
  column,
  gap,
  verticalPadding,
  border,
  align,
  alignText,
  borderColor,
  color,
  showDescriptionText,
  titleTypographyVariants,
  vettingCriteriaList = [
    VETTING_CRITERIA.QUALITY_FABRICS,
    VETTING_CRITERIA.PRECISE_FIT,
    VETTING_CRITERIA.THOUGHTFUL_CONSTRUCTION,
  ],
}) => {
  const { strictLocalize } = useStrictLocalization();
  const vettingCriteria = showroomVettingCriteriaInfo(
    color ?? Core.text.enabled,
    showDescriptionText,
    titleTypographyVariants,
    strictLocalize
  );

  return (
    <>
      {vettingCriteriaList.map((criteriaItem, index) => (
        <FlexWithCustomDirection
          className={className}
          $column={column}
          $gap={gap}
          $verticalPadding={verticalPadding}
          $border={border ? index !== 0 : false}
          key={`${vettingCriteria}_${index}`}
          align={align as unknown as IFlexProps["align"]}
          $borderColor={borderColor}
        >
          <SingleShowroomVettingCriteria
            {...vettingCriteria[criteriaItem]}
            alignText={alignText}
          />
        </FlexWithCustomDirection>
      ))}
    </>
  );
};

// eslint-disable-next-line @faire/prefer-slate-attrs -- move direction and align to props
const FlexWithCustomDirection = styled(Flex).attrs({
  justify: "space-between",
})<{
  $column?: boolean;
  $gap?: ResponsiveProp<number | SpacerMultipliersKey>;
  $verticalPadding?: ResponsiveProp<number | SpacerMultipliersKey>;
  $border?: boolean;
  $borderColor?: string;
}>`
  &&& {
    flex-direction: ${(props) => (props.$column ? "column" : "row")};
    align-items: ${(props) => props.align ?? "center"};
    ${(props) =>
      props.$border
        ? css`
            border-left: 1px solid ${props.$borderColor ?? Core.border.muted};
          `
        : ""};

    ${({ $gap = 0, $verticalPadding: verticalPadding = 0 }) => {
      const horizontalGap: ResponsiveValues<number | SpacerMultipliersKey> =
        isResponsiveProp($gap) ? $gap : { mobileAndAbove: $gap };
      const verticalGap: ResponsiveValues<number | SpacerMultipliersKey> =
        isResponsiveProp(verticalPadding)
          ? verticalPadding
          : { mobileAndAbove: verticalPadding };

      return css`
        padding: ${getSpacing(
          verticalGap.mobileAndAbove,
          horizontalGap.mobileAndAbove
        )};

        ${responsiveMediaQueries.tabletAndAbove} {
          padding: ${getSpacing(
            verticalGap.tabletAndAbove ?? verticalGap.mobileAndAbove,
            horizontalGap.tabletAndAbove ?? horizontalGap.mobileAndAbove
          )};
        }

        ${responsiveMediaQueries.desktopAndAbove} {
          padding: ${getSpacing(
            verticalGap.desktopAndAbove ??
              verticalGap.tabletAndAbove ??
              verticalGap.mobileAndAbove,
            horizontalGap.desktopAndAbove ??
              horizontalGap.tabletAndAbove ??
              horizontalGap.mobileAndAbove
          )};
        }

        ${responsiveMediaQueries.xLargeAndAbove} {
          padding: ${getSpacing(
            verticalGap.xLargeAndAbove ??
              verticalGap.desktopAndAbove ??
              verticalGap.tabletAndAbove ??
              verticalGap.mobileAndAbove,
            horizontalGap.xLargeAndAbove ??
              horizontalGap.desktopAndAbove ??
              horizontalGap.tabletAndAbove ??
              horizontalGap.mobileAndAbove
          )};
        }
      `;
    }}
  }
`;
